import React, { useEffect, useRef } from 'react';
import { Check, Lightbulb, Sliders, Sparkles, X } from 'lucide-react';
import { rgbToHex } from '../../services/colorEngine';
import { animateModalOpen } from '../../animations/gsapEffects';
import { useAmbilightContext } from '../../context/AmbilightContext';

export const PointConfigModal: React.FC = () => {
  const {
    selectedPoint,
    selectedPointId,
    selectPoint,
    points,
    bulbs,
    assignBulbToPoint,
    updatePointBorderWidth,
    applyBorderToAll,
    getBulbForPoint,
  } = useAmbilightContext();

  const modalRef = useRef<HTMLDivElement>(null);
  const isOpen = selectedPointId !== null && selectedPoint !== undefined;

  useEffect(() => {
    if (isOpen && modalRef.current) {
      animateModalOpen(modalRef.current);
    }
  }, [isOpen]);

  if (!isOpen || !selectedPoint) return null;

  const hexColor = rgbToHex(
    selectedPoint.color.r,
    selectedPoint.color.g,
    selectedPoint.color.b
  );

  const assignedBulb = getBulbForPoint(selectedPoint.id);

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) selectPoint(null);
      }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop select-none"
    >
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-2xl modal-card p-6 text-white shadow-2xl border border-white/10"
        style={{
          backgroundColor: '#0f1422',
          backgroundImage: 'linear-gradient(180deg, #141b2d 0%, #0d121f 100%)',
        }}
      >
        {/* Encabezado */}
        <div className="flex items-center justify-between pb-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-full border border-white/30 shadow-md flex items-center justify-center"
              style={{
                backgroundColor: hexColor,
                boxShadow: `0 0 16px ${hexColor}80`,
              }}
            >
              <Sparkles
                className="w-4 h-4"
                style={{
                  color:
                    selectedPoint.color.r * 0.299 +
                      selectedPoint.color.g * 0.587 +
                      selectedPoint.color.b * 0.114 >
                    140
                      ? '#000'
                      : '#fff',
                }}
              />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-white">
                  {selectedPoint.label}
                </h3>
                <span className="px-2 py-0.5 rounded text-[10px] font-semibold tracking-wider uppercase bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">
                  {selectedPoint.shortLabel}
                </span>
              </div>
              <p className="text-xs text-white/50">Zona Perimetral Ambilight</p>
            </div>
          </div>
          <button
            onClick={() => selectPoint(null)}
            className="p-1 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 1. LUZ CORRESPONDIENTE A ESTA ESQUINA / PUNTO */}
        <div className="my-4 p-3.5 rounded-xl bg-gradient-to-r from-amber-500/15 via-purple-500/15 to-transparent border border-amber-500/30 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-400/40 flex items-center justify-center text-amber-300 shadow-md shadow-amber-500/20">
              <Lightbulb className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] text-amber-300/80 font-mono uppercase tracking-wider font-semibold">
                Luz correspondiente a este punto:
              </div>
              <div className="text-sm font-bold text-white flex items-center gap-2 mt-0.5">
                <span>{assignedBulb ? assignedBulb.name : 'Solo pantalla (sin foco físico)'}</span>
                {assignedBulb && (
                  <span className="text-[11px] font-mono px-1.5 py-0.2 rounded bg-amber-400/15 text-amber-300 border border-amber-400/30">
                    {assignedBulb.ip}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="text-right">
            <span
              className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${
                assignedBulb?.connected
                  ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                  : 'bg-slate-800 text-white/50 border-white/10'
              }`}
            >
              {assignedBulb ? (assignedBulb.connected ? 'CONECTADO' : 'OFFLINE') : 'SIN FOCO'}
            </span>
          </div>
        </div>

        {/* 2. Telemetría de Color en Vivo */}
        <div className="mb-4 p-3 rounded-xl bg-white/5 border border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-lg border border-white/20 shadow-inner"
              style={{ backgroundColor: hexColor }}
            />
            <div>
              <div className="font-mono text-xs font-semibold text-white">{hexColor}</div>
              <div className="font-mono text-[11px] text-white/60">
                RGB({selectedPoint.color.r}, {selectedPoint.color.g}, {selectedPoint.color.b})
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs font-medium text-cyan-300">
              Brillo: {selectedPoint.dimming}%
            </div>
            <div className="text-[11px] text-white/50">Lum: {selectedPoint.brightness}%</div>
          </div>
        </div>

        {/* 3. Ancho de Borde de Muestreo para este punto */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between text-xs">
            <span className="text-white/80 font-medium flex items-center gap-1.5">
              <Sliders className="w-3.5 h-3.5 text-cyan-400" />
              Ancho de Borde de Muestreo:
            </span>
            <span className="font-mono font-bold text-cyan-300 bg-cyan-950/60 px-2 py-0.5 rounded border border-cyan-800/40">
              {selectedPoint.borderWidth} px
            </span>
          </div>
          <input
            type="range"
            min={10}
            max={350}
            step={5}
            value={selectedPoint.borderWidth}
            onChange={(e) => updatePointBorderWidth(selectedPoint.id, Number(e.target.value))}
            className="w-full h-1.5 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-cyan-400"
          />
          <div className="flex justify-between items-center pt-0.5 text-[11px]">
            <span className="text-[10px] text-white/40">10px</span>
            <button
              onClick={() => applyBorderToAll(selectedPoint.borderWidth)}
              className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2 transition-colors cursor-pointer text-[11px]"
            >
              Aplicar {selectedPoint.borderWidth}px a los 8 puntos
            </button>
            <span className="text-[10px] text-white/40">350px</span>
          </div>
        </div>

        {/* 4. Cambiar Foco WiZ Asignado a esta esquina */}
        <div className="space-y-1.5 mb-5">
          <label className="text-xs font-medium text-white/80 flex items-center gap-1.5">
            <Lightbulb className="w-3.5 h-3.5 text-amber-400" />
            Asignar Foco a esta esquina:
          </label>
          <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
            {/* Opción Sin Asignar */}
            <div
              onClick={() => assignBulbToPoint(selectedPoint.id, null)}
              className={`flex items-center justify-between p-2.5 rounded-xl border cursor-pointer transition-all ${
                selectedPoint.assignedBulbId === null
                  ? 'border-cyan-400 bg-cyan-500/10 text-white'
                  : 'border-white/10 bg-white/5 text-white/60 hover:bg-white/10'
              }`}
            >
              <div className="flex items-center gap-2 text-xs">
                <span className="w-2 h-2 rounded-full bg-slate-500" />
                <span>Solo Efecto Visual (Pantalla)</span>
              </div>
              {selectedPoint.assignedBulbId === null && <Check className="w-4 h-4 text-cyan-400" />}
            </div>

            {/* Focos Registrados */}
            {bulbs.map((bulb) => {
              const isHere = selectedPoint.assignedBulbId === bulb.id;
              const currentPt = !isHere ? points.find((p) => p.assignedBulbId === bulb.id) : null;

              return (
                <div
                  key={bulb.id}
                  onClick={() => assignBulbToPoint(selectedPoint.id, bulb.id)}
                  className={`flex items-center justify-between p-2.5 rounded-xl border cursor-pointer transition-all ${
                    isHere
                      ? 'border-purple-400 bg-purple-500/15 text-white shadow-sm ring-1 ring-purple-400/30'
                      : 'border-white/10 bg-white/5 text-white/70 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <span
                      className={`w-2.5 h-2.5 rounded-full ${
                        bulb.connected
                          ? 'bg-amber-400 shadow-sm shadow-amber-400 animate-pulse'
                          : 'bg-slate-500'
                      }`}
                    />
                    <div>
                      <div className="text-xs font-semibold text-white flex items-center gap-1.5">
                        <span>{bulb.name}</span>
                        {isHere && (
                          <span className="text-[10px] font-mono text-purple-300 font-normal">
                            (Asignado aquí)
                          </span>
                        )}
                        {currentPt && (
                          <span className="text-[10px] text-amber-300/80 font-normal">
                            (En {currentPt.shortLabel} — se moverá aquí)
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] font-mono text-white/50">{bulb.ip}</div>
                    </div>
                  </div>
                  {isHere && <Check className="w-4 h-4 text-purple-400" />}
                </div>
              );
            })}
          </div>
        </div>

        <button
          onClick={() => selectPoint(null)}
          className="w-full py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-semibold text-xs shadow-lg shadow-purple-600/30 transition-all hover:scale-[1.01] active:scale-[0.99] cursor-pointer"
        >
          Listo
        </button>
      </div>
    </div>
  );
};
